import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Clock, CheckCircle, XCircle, LogOut, RefreshCw, Trash2, Phone, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const HELPLINE_NUMBER = "7070301098";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_social-circle-37/artifacts/vzzvahrb_IMG-20251227-WA0000.jpg";

const PendingVerificationPage = () => {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Check verification status
  const checkStatus = async () => {
    try {
      const response = await api.get("/auth/me");
      updateUser(response.data);
      
      if (response.data.verification_status === "verified") {
        toast.success("Your profile has been verified!");
        navigate("/feed");
      } else if (response.data.verification_status === "rejected") {
        toast.error("Your profile was not approved. Please contact support.");
      }
    } catch (error) {
      console.error("Failed to check status", error);
    }
  };

  // Handle contact helpline
  const handleContactHelpline = () => {
    window.location.href = `tel:${HELPLINE_NUMBER}`;
  };

  // Handle removing/deleting the profile
  const handleRemoveProfile = async () => {
    setDeleting(true);
    try {
      await api.delete("/users/profile");
      toast.success("Your profile has been removed successfully.");
      logout();
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove profile");
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  useEffect(() => {
    // Redirect if already verified
    if (user?.verification_status === "verified") {
      navigate("/feed");
    }
    
    // Poll for status changes every 10 seconds
    const interval = setInterval(checkStatus, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const getStatusContent = () => {
    const status = user?.verification_status || "pending";
    
    switch (status) {
      case "verified":
        return {
          icon: <CheckCircle className="w-20 h-20 text-green-500" />,
          title: "Profile Verified!",
          description: "Your profile has been verified. You can now access all features.",
          color: "text-green-600",
          bgColor: "bg-green-50 dark:bg-green-950/30",
          borderColor: "border-green-200 dark:border-green-800"
        };
      case "rejected":
        return {
          icon: <XCircle className="w-20 h-20 text-red-500" />,
          title: "Verification Rejected",
          description: user?.verification_note || "Your profile could not be verified. Please contact the administrator for more information.",
          color: "text-red-600",
          bgColor: "bg-red-50 dark:bg-red-950/30",
          borderColor: "border-red-200 dark:border-red-800"
        };
      default:
        return {
          icon: <Clock className="w-20 h-20 text-amber-500 animate-pulse" />,
          title: "Verification Pending",
          description: "Your profile is being reviewed by our team. This usually takes 24-48 hours. You will be notified once your profile is verified.",
          color: "text-amber-600",
          bgColor: "bg-amber-50 dark:bg-amber-950/30",
          borderColor: "border-amber-200 dark:border-amber-800"
        };
    }
  };

  const statusContent = getStatusContent();

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative" data-testid="pending-verification-page">
      {/* Watermark */}
      <div className="fixed inset-0 pointer-events-none z-0 flex items-center justify-center">
        <img 
          src={LOGO_URL} 
          alt="Lunawat Foundation" 
          className="w-64 h-64 md:w-96 md:h-96 object-contain opacity-5"
        />
      </div>
      
      <div className="w-full max-w-lg relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src={LOGO_URL} alt="Logo" className="w-12 h-12 rounded-full object-cover" />
            <span className="text-2xl font-bold">LUNAWAT FOUNDATION</span>
          </div>
        </div>

        <Card className={`shadow-lg border-2 ${statusContent.borderColor}`}>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              {statusContent.icon}
            </div>
            <CardTitle className={`text-2xl ${statusContent.color}`}>
              {statusContent.title}
            </CardTitle>
            <CardDescription className="text-base mt-2">
              {statusContent.description}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* User Info Summary */}
            <div className={`p-4 rounded-lg ${statusContent.bgColor} mb-6`}>
              <h3 className="font-semibold mb-3">Profile Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Name:</span>
                  <span className="font-medium">{user?.first_name} {user?.last_name || "LUNAWAT"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Phone:</span>
                  <span className="font-medium">{user?.phone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Location:</span>
                  <span className="font-medium">{user?.city}, {user?.state}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Occupation:</span>
                  <span className="font-medium capitalize">{user?.occupation}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              {user?.verification_status === "verified" && (
                <Button 
                  onClick={() => navigate("/feed")} 
                  className="w-full"
                  data-testid="go-to-feed-btn"
                >
                  Go to Feed
                </Button>
              )}
              
              {user?.verification_status === "pending" && (
                <Button 
                  variant="outline" 
                  onClick={checkStatus} 
                  className="w-full"
                  data-testid="refresh-status-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Check Status
                </Button>
              )}

              {user?.verification_status === "rejected" && (
                <>
                  <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-3">
                    <p className="text-sm text-amber-800 dark:text-amber-200 text-center">
                      What would you like to do?
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Button 
                      variant="outline"
                      onClick={handleContactHelpline} 
                      className="w-full border-green-500 text-green-600 hover:bg-green-50"
                      data-testid="contact-helpline-btn"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Call Helpline
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => setShowDeleteDialog(true)} 
                      className="w-full border-red-500 text-red-600 hover:bg-red-50"
                      data-testid="remove-profile-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove Profile
                    </Button>
                  </div>
                  <p className="text-xs text-center text-muted-foreground mt-2">
                    Helpline: {HELPLINE_NUMBER}
                  </p>
                </>
              )}

              <Button 
                variant="ghost" 
                onClick={handleLogout} 
                className="w-full text-muted-foreground"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>

            {/* Contact Info */}
            {user?.verification_status !== "verified" && (
              <p className="text-center text-sm text-muted-foreground mt-6">
                Need help? Contact administrator for assistance.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Remove Profile Permanently?
            </DialogTitle>
            <DialogDescription className="pt-2">
              This action cannot be undone. Your profile and all associated data will be permanently deleted from our system.
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4 my-4">
            <p className="text-sm text-red-800 dark:text-red-200">
              <strong>Warning:</strong> The following will be deleted:
            </p>
            <ul className="text-sm text-red-700 dark:text-red-300 mt-2 list-disc list-inside space-y-1">
              <li>Your profile information</li>
              <li>All your messages and conversations</li>
              <li>Your poll votes</li>
              <li>All associated data</li>
            </ul>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleRemoveProfile}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Yes, Remove My Profile
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PendingVerificationPage;
